<template>
  <div class="standard-model-pro">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex> \$2\$ </div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage">
        <div class="particle-dashboard">
           <!-- Quarks Group -->
           <div class="group-lane">
              <span class="lane-label">QUARKS (夸克)</span>
              <div class="grid-3x2">
                 <div v-for="q in quarks" :key="q.id" 
                      class="particle-cell quark" :class="{ active: selected === q }"
                      @mouseover="selected = q">
                    <span class="symbol">{{ q.symbol }}</span>
                    <span class="sub">{{ q.name }}</span>
                 </div>
              </div>
           </div>

           <!-- Leptons Group -->
           <div class="group-lane">
              <span class="lane-label">LEPTONS (轻子)</span>
              <div class="grid-3x2">
                 <div v-for="l in leptons" :key="l.id" 
                      class="particle-cell lepton" :class="{ active: selected === l }"
                      @mouseover="selected = l">
                    <span class="symbol">{{ l.symbol }}</span>
                    <span class="sub">{{ l.name }}</span>
                 </div>
              </div>
           </div>

           <!-- Bosons Group -->
           <div class="group-lane center-lane">
              <span class="lane-label">BOSONS (波色子)</span>
              <div class="grid-vertical">
                 <div v-for="b in bosons" :key="b.id" 
                      class="particle-cell boson" :class="{ active: selected === b }"
                      @mouseover="selected = b">
                    <span class="symbol">{{ b.symbol }}</span>
                    <span class="sub">{{ b.name }}</span>
                 </div>
              </div>
           </div>

           <!-- Scalar (Higgs) -->
           <div class="group-lane scalar-lane">
              <span class="lane-label">SCALAR</span>
              <div class="particle-cell higgs" :class="{ active: selected === higgs }"
                   @mouseover="selected = higgs">
                 <span class="symbol">H</span>
                 <span class="sub">Higgs</span>
              </div>
           </div>
        </div>

        <!-- Info Overlay -->
        <transition name="fade">
          <div class="info-card" v-if="selected">
             <div class="info-header">
                <span class="type-badge" :style="{ background: typeColor }">{{ selected.type }}</span>
                <h3 :style="{ color: typeColor }">{{ selected.fullName }} ({{ selected.symbol }})</h3>
             </div>
             <div class="info-body">
                <div class="stat"><label>质量:</label> <span>{{ selected.mass }}</span></div>
                <div class="stat"><label>电荷:</label> <span>{{ selected.charge }}</span></div>
                <div class="stat"><label>自旋:</label> <span>{{ selected.spin }}</span></div>
                <p class="desc">{{ selected.desc }}</p>
             </div>
          </div>
        </transition>
      </div>

      <div class="compact-controls">
        <div class="discovery-card-compact" v-latex>
           <i class="el-icon-help"></i>
           <p><strong>物理详解：</strong>标准模型是描述强、弱、电磁三种力及基本粒子的理论。<strong>夸克</strong> 参与强相互作用（构成核子）；<strong>轻子</strong>（如电子）不参与强力。<strong>玻色子</strong>是力传导者，而 <strong>希格斯场</strong> 赋予了这些粒子质量。这是目前人类对微观宇宙最深刻的理解。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'StandardModelViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      selected: null,
      quarks: [
        { id: 1, symbol: 'u', name: 'Up', fullName: '上夸克', type: 'Quark', mass: '2.2 MeV', charge: '+2/3', spin: '1/2', desc: '构成质子与中子的最轻夸克。' },
        { id: 2, symbol: 'c', name: 'Charm', fullName: '粲夸克', type: 'Quark', mass: '1.27 GeV', charge: '+2/3', spin: '1/2', desc: '第二代夸克，较重。' },
        { id: 3, symbol: 't', name: 'Top', fullName: '顶夸克', type: 'Quark', mass: '173 GeV', charge: '+2/3', spin: '1/2', desc: '最重的基本粒子，寿命极短。' },
        { id: 4, symbol: 'd', name: 'Down', fullName: '下夸克', type: 'Quark', mass: '4.7 MeV', charge: '-1/3', spin: '1/2', desc: '与上夸克共同构成原子核。' },
        { id: 5, symbol: 's', name: 'Strange', fullName: '奇夸克', type: 'Quark', mass: '95 MeV', charge: '-1/3', spin: '1/2', desc: '在奇异强子中发现。' },
        { id: 6, symbol: 'b', name: 'Bottom', fullName: '底夸克', type: 'Quark', mass: '4.18 GeV', charge: '-1/3', spin: '1/2', desc: '参与弱相互作用的混合。' }
      ],
      leptons: [
        { id: 7, symbol: 'e', name: 'Electron', fullName: '电子', type: 'Lepton', mass: '0.511 MeV', charge: '-1', spin: '1/2', desc: '构成原子壳层的稳定轻子。' },
        { id: 8, symbol: 'μ', name: 'Muon', fullName: '缪子', type: 'Lepton', mass: '105.7 MeV', charge: '-1', spin: '1/2', desc: '电子的重“表亲”，不稳定。' },
        { id: 9, symbol: 'τ', name: 'Tau', fullName: '陶子', type: 'Lepton', mass: '1.77 GeV', charge: '-1', spin: '1/2', desc: '最重的带电轻子。' },
        { id: 10, symbol: 'νe', name: 'Nu e', fullName: '电子中微子', type: 'Lepton', mass: '< 1 eV', charge: '0', spin: '1/2', desc: '几乎不与物质作用的幽灵粒子。' },
        { id: 11, symbol: 'νμ', name: 'Nu mu', fullName: '缪中微子', type: 'Lepton', mass: '< 1 eV', charge: '0', spin: '1/2', desc: '伴随缪子衰变产生。' },
        { id: 12, symbol: 'ντ', name: 'Nu tau', fullName: '陶中微子', type: 'Lepton', mass: '< 1 eV', charge: '0', spin: '1/2', desc: '最晚被证实的轻子伙伴。' }
      ],
      bosons: [
        { id: 13, symbol: 'g', name: 'Gluon', fullName: '胶子', type: 'Boson', mass: '0', charge: '0', spin: '1', desc: '传递强核力，将夸克锁在一起。' },
        { id: 14, symbol: 'γ', name: 'Photon', fullName: '光子', type: 'Boson', mass: '0', charge: '0', spin: '1', desc: '传递电磁力。' },
        { id: 15, symbol: 'Z', name: 'Z', fullName: 'Z玻色子', type: 'Boson', mass: '91.2 GeV', charge: '0', spin: '1', desc: '传递弱相互作用的中性介子。' },
        { id: 16, symbol: 'W', name: 'W', fullName: 'W玻色子', type: 'Boson', mass: '80.4 GeV', charge: '±1', spin: '1', desc: '改变粒子风味的弱力载体。' }
      ],
      higgs: { id: 17, symbol: 'H', name: 'Higgs', fullName: '希格斯玻色子', type: 'Scalar', mass: '125 GeV', charge: '0', spin: '0', desc: '赋予其他基本粒子质量的万物之源。' }
    }
  },
  computed: {
    typeColor() {
       if (!this.selected) return '#94a3b8';
       const colors = { Quark: '#f43f5e', Lepton: '#10b981', Boson: '#3b82f6', Scalar: '#fbbf24' };
       return colors[this.selected.type] || '#fff';
    }
  }
}
</script>

<style scoped lang="scss">
.standard-model-pro { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }

.viz-header-compact {
  display: flex; justify-content: space-between; align-items: center; padding: 0 4px;
  .brief { font-size: 13.5px; color: #1e293b; font-weight: 500; }
  .formula-chip { background: #f0f9ff; color: #0369a1; border: 1px solid #bae6fd; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; }
}

.simulation-stage {
  flex: 1; min-height: 320px; background: radial-gradient(circle at center, #0f172a, #020617); border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden;
  display: flex; flex-direction: column; justify-content: space-evenly; padding: 10px;
}

.particle-dashboard { display: flex; justify-content: space-around; align-items: flex-start; gap: 5px; }
.group-lane { display: flex; flex-direction: column; align-items: center; gap: 8px; }
.lane-label { font-size: 9px; color: #64748b; font-weight: 900; letter-spacing: 1px; }

.grid-3x2 { display: grid; grid-template-columns: repeat(3, 40px); grid-template-rows: repeat(2, 40px); gap: 6px; }
.grid-vertical { display: flex; flex-direction: column; gap: 6px; }

.particle-cell {
  width: 40px; height: 40px; border-radius: 8px; border: 1.5px solid rgba(255,255,255,0.05);
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  background: rgba(30, 41, 59, 0.5); cursor: pointer; transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  .symbol { font-size: 14px; font-weight: 900; }
  .sub { font-size: 8px; opacity: 0.6; font-weight: bold; }
  &:hover, &.active { transform: scale(1.1); border-color: #fff; box-shadow: 0 0 15px currentColor; z-index: 5; }
}

.quark { color: #f43f5e; background: rgba(244, 63, 94, 0.1); }
.lepton { color: #10b981; background: rgba(16, 185, 129, 0.1); }
.boson { color: #3b82f6; background: rgba(59, 130, 246, 0.1); }
.higgs { color: #fbbf24; background: rgba(251, 191, 36, 0.1); width: 45px; height: 45px; }

.info-card {
  position: absolute; bottom: 12px; left: 12px; right: 12px; background: rgba(15, 23, 42, 0.95);
  border: 1px solid #1e293b; border-radius: 10px; padding: 12px 16px; backdrop-filter: blur(8px);
  pointer-events: none;
  .info-header {
    display: flex; align-items: center; gap: 10px; margin-bottom: 8px;
    .type-badge { font-size: 9px; padding: 2px 6px; border-radius: 4px; color: #fff; font-weight: bold; text-transform: uppercase; }
    h3 { margin: 0; font-size: 15px; }
  }
}

.info-body {
   display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 8px;
   .stat { color: #94a3b8; font-size: 11px; label { font-weight: bold; margin-right: 4px; } span { color: #f1f5f9; font-weight: 900; } }
   .desc { grid-column: span 3; margin: 4px 0 0 0; font-size: 11px; color: #cbd5e1; line-height: 1.4; opacity: 0.8; }
}

.fade-enter-active, .fade-leave-active { transition: opacity 0.3s; }
.fade-enter, .fade-leave-to { opacity: 0; }

.discovery-card-compact {
  background: #f8fafc; padding: 10px 14px; border-radius: 10px; border: 1px solid #e2e8f0; display: flex; align-items: center; gap: 10px;
  i { color: #3b82f6; font-size: 1.1rem; }
  p { flex: 1; margin: 0; font-size: 12px; color: #475569; line-height: 1.5; strong { color: #1e293b; } }
}
</style>
